import { useState } from 'react';
import type { PageModeration } from '@/hooks/use-page-moderation';
import { Filter, LayoutGrid, List } from 'lucide-react';

import { Button } from '@repo/ui/components/ui/button';
import { ConfirmationDialog } from '@repo/ui/components/ui/confirmation-dialog';
import { cn } from '@repo/ui/lib/utils';
import { usePhotos } from '@/state/use-photos';
import { usePhotoSelection, ViewMode } from '@/state/use-photo-selection';
import type { BulkPhotoAction, PhotoSort } from '@/services/photovogue';
import { BULK_PHOTO_ACTIONS } from '@/components/photovogue/moderation-actions';
import { FilterPanel } from '@/components/photovogue/filter-panel/component';
import type { PhotoView } from '@/components/photovogue/photo-grid/component';

const formatShort = (key?: string) =>
  key
    ? new Date(`${key}T00:00:00`).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : '…';

export interface ModerationToolbarProps {
  moderation: PageModeration;
  view: PhotoView;
  onViewChange: (view: PhotoView) => void;
}

export const ModerationToolbar = ({
  view,
  onViewChange,
  moderation,
}: ModerationToolbarProps) => {
  const {
    data,
    loadingSpinner,
    error,
    count,
    params,
    updateSort,
    moderatePhotos,
    hasActiveFilters,
    resetFilters,
  } = usePhotos();
  const { viewMode, selectedIds, toggleViewMode, selectAll, deselectAll } =
    usePhotoSelection();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const selecting = !moderation.active && viewMode === ViewMode.SELECT;

  const onBulk = (action: BulkPhotoAction) => {
    if (!selectedIds.length) return;
    moderatePhotos(selectedIds, action);
    deselectAll();
  };

  const sortValue = `${params.sort.field}:${params.sort.direction}`;
  const onSortChange = (value: string) => {
    if (!moderation.discard()) return;
    const [field, direction] = value.split(':') as [
      PhotoSort['field'],
      PhotoSort['direction'],
    ];
    updateSort({ field, direction });
  };

  return (
    <div className="sticky top-2 z-20 mb-4 overflow-hidden rounded-xl border bg-white shadow-md">
      <div className="flex flex-wrap items-center gap-2 p-3">
        <span className="text-sm text-cn-muted-foreground">
          {count} photo{count === 1 ? '' : 's'}
        </span>

        {(params.filters.uploadedFrom || params.filters.uploadedTo) && (
          <span className="rounded-md border bg-white px-2 py-1 text-xs text-cn-muted-foreground">
            Uploaded {formatShort(params.filters.uploadedFrom)} –{' '}
            {formatShort(params.filters.uploadedTo)}
          </span>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={moderation.saving}
            onClick={() => {
              if (moderation.discard()) setFiltersOpen(true);
            }}
          >
            <Filter /> Filters
          </Button>
          {hasActiveFilters() && (
            <Button
              variant="ghost"
              size="sm"
              disabled={moderation.saving}
              onClick={() => {
                if (moderation.discard()) resetFilters();
              }}
            >
              Clear filters
            </Button>
          )}

          <select
            disabled={moderation.saving}
            value={sortValue}
            onChange={(event) => onSortChange(event.target.value)}
            className="h-8 w-32 rounded-md border border-input sm:w-auto bg-transparent px-2 text-xs"
            aria-label="Sort photos"
          >
            <option value="createdAt:desc">Newest</option>
            <option value="createdAt:asc">Oldest</option>
            <option value="stateUpdatedAt:desc">Recently moderated</option>
            <option value="title:asc">Title A–Z</option>
          </select>

          <div className="flex items-center overflow-hidden rounded-md border">
            <button
              type="button"
              onClick={() => onViewChange('grid')}
              className={cn(
                'p-1.5',
                view === 'grid'
                  ? 'bg-zinc-900 text-white'
                  : 'text-cn-muted-foreground hover:bg-zinc-100',
              )}
              aria-label="Grid view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onViewChange('list')}
              className={cn(
                'p-1.5',
                view === 'list'
                  ? 'bg-zinc-900 text-white'
                  : 'text-cn-muted-foreground hover:bg-zinc-100',
              )}
              aria-label="List view"
            >
              <List className="h-4 w-4" />
            </button>
          </div>

          {!moderation.active && (
            <Button
              disabled={loadingSpinner || Boolean(error) || !data.length}
              variant="default"
              size="sm"
              onClick={() => {
                if (selecting) toggleViewMode();
                else deselectAll();
                moderation.start(data);
              }}
            >
              Moderate page
            </Button>
          )}
          <Button
            disabled={moderation.saving}
            variant={selecting ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => {
              if (moderation.active) moderation.discard();
              else toggleViewMode();
            }}
          >
            {moderation.active
              ? 'Exit moderation'
              : selecting
                ? 'Cancel'
                : 'Select'}
          </Button>
        </div>
      </div>

      {moderation.active && (
        <div className="flex flex-wrap items-center gap-2 border-t p-3">
          <span className="text-sm font-medium" aria-live="polite">
            <span className="text-green-700">
              {moderation.selectedIds.length} to approve
            </span>
            {' · '}
            <span className="text-red-700">
              {(moderation.photos?.length ?? 0) -
                moderation.selectedIds.length -
                moderation.ignoredIds.length}{' '}
              to reject
            </span>
            {' · '}
            {moderation.ignoredIds.length} ignored{' · Current page only'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={moderation.saving}
            onClick={moderation.selectAll}
          >
            Approve all
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={moderation.saving}
            onClick={moderation.clear}
          >
            Reject all
          </Button>
          <Button
            className="ml-auto"
            size="sm"
            disabled={!moderation.canSave}
            onClick={moderation.save}
          >
            {moderation.saving
              ? 'Saving…'
              : moderation.retrying
                ? 'Retry remaining decisions'
                : 'Save decisions'}
          </Button>
          <p className="w-full text-xs text-cn-muted-foreground">
            Choose Approve, Reject, or Ignore for each photo. Ignored photos are
            excluded from this save and keep their current state.
          </p>
        </div>
      )}

      {selecting && (
        <div className="flex flex-wrap items-center gap-2 border-t p-3">
          <span className="text-sm font-medium">
            {selectedIds.length} selected
          </span>
          <Button variant="ghost" size="sm" onClick={selectAll}>
            Approve all
          </Button>
          <Button variant="ghost" size="sm" onClick={deselectAll}>
            Clear
          </Button>

          <div className="ml-auto flex flex-wrap gap-2">
            {BULK_PHOTO_ACTIONS.map((item) =>
              item.destructive ? (
                <ConfirmationDialog
                  key={item.action}
                  title={`Ban ${selectedIds.length} photo(s)?`}
                  description="This sets their state to banned."
                  actionName="Ban"
                  permission=""
                  action={() => onBulk(item.action)}
                >
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={!selectedIds.length}
                  >
                    {item.label}
                  </Button>
                </ConfirmationDialog>
              ) : (
                <Button
                  key={item.action}
                  variant={item.variant}
                  size="sm"
                  disabled={!selectedIds.length}
                  onClick={() => onBulk(item.action)}
                >
                  {item.label}
                </Button>
              ),
            )}
          </div>
        </div>
      )}

      <FilterPanel open={filtersOpen} onOpenChange={setFiltersOpen} />
    </div>
  );
};

export default ModerationToolbar;
